// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/orders/:ref — Order details, the staff work screen for one order)
// After the Command Ops order-details mockup. REAL, from GET /api/admin/orders/{ref}: the order header (status, payment mode, placed time), the
// customer card (delivery name, phone with call / WhatsApp links, order and complaint counts, address, delivery notes), the catch manifest
// (photo, pack, cut, quantity, prices), the settlement ledger (subtotal, delivery, discount, wallet, total, paid online + Razorpay id, invoice
// number, refund state), the status timeline with the next step highlighted, any complaint and the customer's review, and a PRIVATE ops note
// (PUT …/note; editors only, never shown to the customer). Editors/admins move the order to its next step or cancel it (the customer is refunded
// through the normal backend path); viewers see everything read-only. "Print packing slip" uses the browser's print.
// Coming-soon sample panels (SoonPanel): cold-chain probe, route map, bulk actions, batch banner. NOT shown (no data behind
// them): delivery slot, Catch Pass VIP / rating / lifetime totals, alternate phone, drop zone, net weights and quality scans, chiller/pod/probe
// ids, a free spice add-on, eco-box deposit, GST / audit-fee lines, "100 % escrow", live traffic, assigned captain names, barcodes / cryo-seal codes.
// Neighbours: components/admin/AdminParts, utils/adminApi, pages/admin/AdminOrders.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, CircleAlert, CircleCheck, Loader2, MessageCircle, Phone, Printer, Star, X } from "lucide-react";
import { SoonPanel, panel } from "../../components/admin/AdminParts";
import { seafoodItems } from "../../data/seafood";
import useAuth from "../../hooks/useAuth";
import { ApiError } from "../../utils/api";
import { NEXT, fetchOrderDetail, saveOpsNote, setOrderStatus, type OrderDetail } from "../../utils/adminApi";
import { fetchReasons, type Reason } from "../../utils/cancelReasons";
import { reasonLabel } from "../../utils/claims";
import { STATUS_LABEL, formatWhen, paidVia, type OrderStatus } from "../../utils/orders";
import { formatInr } from "../../utils/priceFormat";
import { getWikimediaSrcSet } from "../../utils/wikimediaImage";

const FLOW: OrderStatus[] = ["placed", "confirmed", "preparing", "out_for_delivery", "delivered"];
const VERB: Partial<Record<OrderStatus, string>> = { confirmed: "Confirm order", preparing: "Start preparing", out_for_delivery: "Send out for delivery", delivered: "Mark delivered" };
const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("") || "?";
const digits = (phone: string) => phone.replace(/\D/g, "");

const Row = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <div className={`flex justify-between gap-3 py-1 text-sm ${strong ? "font-bold" : ""}`}><span>{label}</span><span>{value}</span></div>
);

export default function AdminOrderDetail() {
  const { ref = "" } = useParams();
  const auth = useAuth();
  const canWrite = auth.user?.role === "admin" || auth.user?.role === "editor";
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [failed, setFailed] = useState<"missing" | "error" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  // Reasons staff have published for the "Ops & admin" persona (none = nothing to choose).
  const [adminReasons, setAdminReasons] = useState<Reason[]>([]);
  const [reasonCode, setReasonCode] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  useEffect(() => {
    if (!cancelling) return;
    let gone = false;
    fetchReasons().then((v) => !gone && setAdminReasons(v.saved ? v.reasons.filter((r) => r.persona === "admin" && r.active) : []), () => undefined);
    return () => { gone = true; };
  }, [cancelling]);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const loaded = await fetchOrderDetail(ref);
      setDetail(loaded);
      setNote(loaded.opsNote ?? "");
      setFailed(null);
    } catch (caught) {
      setFailed(caught instanceof ApiError && caught.status === 404 ? "missing" : "error");
    }
  }, [ref]);

  useEffect(() => {
    setDetail(null);
    void load();
  }, [load]);

  if (failed) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">{failed === "missing" ? "That order doesn't exist." : "We couldn't load this order. Reload to try again."}</p>
        <Link to="/admin/orders" className="inline-flex min-h-11 items-center gap-1 font-bold underline"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to orders</Link>
      </div>
    );
  }
  if (!detail) return <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>;

  const { order } = detail;
  const next = NEXT[order.status];
  const step = FLOW.indexOf(order.status);
  const paidOnline = order.payment ? order.total - order.walletAmount : 0;

  const move = async (target: OrderStatus) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await setOrderStatus(order.ref, target, target === "cancelled" ? reasonCode || undefined : undefined, target === "cancelled" ? reasonNote.trim() || undefined : undefined);
      setCancelling(false);
      await load();
      setNotice(target === "cancelled" ? "The order was cancelled and the refund started." : `Order moved to “${STATUS_LABEL[target]}”.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't change that order.");
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await saveOpsNote(order.ref, note);
      setDetail(saved);
      setNotice(note.trim() ? "Note saved." : "Note cleared.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save the note.");
    } finally {
      setBusy(false);
    }
  };

  const actions = canWrite && order.active && (
    <>
      <button type="button" onClick={() => setCancelling(true)} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-coral-500/10 px-4 text-sm font-bold text-coral-600 disabled:opacity-60 dark:text-coral-300"><X className="h-4 w-4" aria-hidden="true" /> Cancel order</button>
      {next && <button type="button" onClick={() => void move(next)} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-700 px-5 text-sm font-bold text-white shadow-md disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />} {VERB[next]}</button>}
    </>
  );

  return (
    <div className="flex flex-col gap-5 pb-28">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
          <Link to="/admin/orders" className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-ocean-700/10 px-3 font-bold dark:bg-white/10"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to orders</Link>
          <span aria-hidden="true">/</span><span className="text-xs font-bold uppercase tracking-wider">Order management</span><span aria-hidden="true">/</span><span className="font-bold">#{order.ref}</span>
        </nav>
        <p className="text-xs">Placed {formatWhen(order.createdAt)}</p>
      </div>

      <header className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl font-bold">Order #{order.ref}</h1>
            {order.cancellation && <span data-testid="cancel-reason-shown" className="text-xs font-semibold">Reason: {order.cancellation.label} ({order.cancellation.by === "customer" ? "customer" : "staff"}){order.cancellation.note ? ` — “${order.cancellation.note}”` : ""}</span>}
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${order.status === "placed" ? "bg-coral-500/15 text-coral-600 dark:text-coral-300" : order.status === "cancelled" ? "bg-ocean-700/10" : "bg-teal-100 text-teal-700"}`} data-testid="order-status">{order.status === "placed" ? "Pending confirmation" : STATUS_LABEL[order.status]}</span>
            <span className="rounded-full bg-ocean-700/10 px-3 py-1 text-xs font-bold dark:bg-white/10" data-testid="paid-via">{paidVia(order)}</span>
          </div>
          <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{order.items.length} line {order.items.length === 1 ? "item" : "items"} · {order.delivery.city} · invoice {order.invoiceNumber ?? "—"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <button type="button" disabled title="Coming soon" className="min-h-11 cursor-not-allowed rounded-lg bg-ocean-700/10 px-4 text-sm font-bold opacity-60 dark:bg-white/10">Bulk actions <span className="text-[10px] uppercase text-coral-600 dark:text-coral-300">soon</span></button>
          <button type="button" onClick={() => window.print()} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10"><Printer className="h-4 w-4" aria-hidden="true" /> Print packing slip</button>
          {actions}
        </div>
      </header>

      <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
      <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">{error && <CircleAlert className="h-4 w-4" aria-hidden="true" />}{error}</p>
      {cancelling && (
        <div role="alertdialog" aria-label={`Cancel order ${order.ref}?`} className="rounded-2xl bg-coral-500/10 p-4">
          <p className="font-bold">Cancel order #{order.ref}?</p>
          <p className="mt-1 text-sm">The customer is refunded {formatInr(order.total)}: the online part to the original payment method{order.walletAmount > 0 ? ` and ${formatInr(order.walletAmount)} back to their wallet` : ""}. This can&apos;t be undone.</p>
          {adminReasons.length > 0 && (
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-bold">Reason for cancelling
                <select data-testid="admin-cancel-reason" value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} className="mt-1 min-h-11 w-full rounded-lg bg-white px-3 text-sm font-normal ring-1 ring-ocean-900/15 dark:bg-ocean-950 dark:ring-white/20">
                  <option value="">Choose a reason…</option>
                  {adminReasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
                </select>
              </label>
              {adminReasons.find((r) => r.code === reasonCode)?.requiresNote && (
                <label className="block text-xs font-bold">Note<textarea data-testid="admin-cancel-note" value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} maxLength={300} rows={2} className="mt-1 w-full rounded-lg bg-white px-3 py-2 text-sm font-normal ring-1 ring-ocean-900/15 dark:bg-ocean-950 dark:ring-white/20" /></label>
              )}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button type="button" disabled={busy || (adminReasons.length > 0 && reasonCode === "")} onClick={() => void move("cancelled")} className="min-h-11 rounded-lg bg-coral-600 px-5 text-sm font-bold text-white disabled:opacity-60">Yes, cancel and refund</button>
            <button type="button" onClick={() => setCancelling(false)} className="min-h-11 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10">Keep the order</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="flex flex-col gap-5 xl:col-span-8">
          <section aria-labelledby="cust-h" className={panel}>
            <h2 id="cust-h" className="sr-only">Customer</h2>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
              <div className="flex items-center gap-3">
                <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-ocean-800 font-bold text-white">{initials(order.delivery.name)}</span>
                <div><p className="font-display text-lg font-bold" data-testid="cust-name">{order.delivery.name}</p><p className="text-xs" data-testid="cust-counts">{detail.customerOrderCount} {detail.customerOrderCount === 1 ? "order" : "orders"} · {detail.customerComplaintCount} {detail.customerComplaintCount === 1 ? "complaint" : "complaints"}</p></div>
              </div>
              <p className="break-all rounded-lg bg-ocean-800 px-3 py-1.5 text-xs font-bold text-white">Customer ID: {detail.customerUid}</p>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
                <p className="text-[11px] font-bold uppercase tracking-wider">Contact</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <p className="font-bold">{order.delivery.phone}</p>
                  <span className="flex gap-1.5">
                    <a href={`tel:${order.delivery.phone}`} aria-label={`Call ${order.delivery.name}`} className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-ocean-900 shadow-sm dark:bg-ocean-900 dark:text-foam-50"><Phone className="h-4 w-4" aria-hidden="true" /></a>
                    <a href={`https://wa.me/${digits(order.delivery.phone)}`} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${order.delivery.name} (opens in a new tab)`} className="flex h-11 w-11 items-center justify-center rounded-lg bg-whatsapp text-ocean-950 shadow-sm"><MessageCircle className="h-4 w-4" aria-hidden="true" /></a>
                  </span>
                </div>
              </div>
              <div className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
                <p className="text-[11px] font-bold uppercase tracking-wider">Drop location</p>
                <p className="mt-2 text-sm font-semibold">{order.delivery.addressLine}, {order.delivery.city} - {order.delivery.pincode}</p>
                {order.delivery.notes.length > 0 && <p className="mt-2 rounded-lg bg-ocean-700/10 p-2 text-xs dark:bg-white/10"><strong>Delivery requests:</strong> {order.delivery.notes.join(" · ")}</p>}
              </div>
            </div>
          </section>

          <section aria-labelledby="man-h" className={panel}>
            <div className="flex flex-wrap items-center gap-2"><h2 id="man-h" className="font-display text-lg font-bold">Catch manifest &amp; prep</h2><span className="rounded-full bg-ocean-700/10 px-2.5 py-0.5 text-xs font-bold dark:bg-white/10">{order.items.length} line {order.items.length === 1 ? "item" : "items"}</span></div>
            <ul className="mt-3 flex flex-col gap-3">
              {order.items.map((line) => {
                const item = seafoodItems.find((candidate) => candidate.id === line.id);
                return (
                  <li key={`${line.id}-${line.pack}`} className="flex flex-col justify-between gap-3 rounded-xl bg-ocean-700/5 p-4 md:flex-row md:items-center dark:bg-white/5" data-testid="manifest-line">
                    <div className="flex items-center gap-4">
                      {item ? <img src={item.image} srcSet={getWikimediaSrcSet(item.image)} sizes="80px" alt="" width={80} height={80} loading="lazy" className="h-20 w-20 rounded-xl object-cover" /> : <span aria-hidden="true" className="h-20 w-20 rounded-xl bg-ocean-700/10" />}
                      <div>
                        <p className="font-display text-base font-bold">{line.name}</p>
                        <p className="text-sm"><span className="font-semibold">Cut:</span> {line.cut}</p>
                        <p className="text-sm">{line.qty} × {line.pack === "1kg" ? "1 kg" : "500 g"} pack{line.qty === 1 ? "" : "s"}</p>
                      </div>
                    </div>
                    <div className="text-right"><p className="text-xs">{formatInr(line.unitPrice)} / pack</p><p className="font-display text-lg font-bold">{formatInr(line.lineTotal)}</p></div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4 rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5" aria-label="Settlement ledger">
              <p className="text-[11px] font-bold uppercase tracking-wider">Settlement ledger</p>
              <Row label="Items subtotal" value={formatInr(order.subtotal)} />
              <Row label="Delivery" value={order.deliveryFee === 0 ? "Free" : formatInr(order.deliveryFee)} />
              {order.discount > 0 && <Row label={`Discount${order.couponCode ? ` (${order.couponCode})` : ""}`} value={`−${formatInr(order.discount)}`} />}
              {order.walletAmount > 0 && <Row label="Paid from wallet" value={formatInr(order.walletAmount)} />}
              {order.payment && <Row label={`Paid online${order.payment.method ? ` (${order.payment.method})` : ""}`} value={formatInr(paidOnline)} />}
              <Row label="Order total" value={formatInr(order.total)} strong />
              <p className="mt-1 break-all text-xs">{order.payment ? `Razorpay payment ${order.payment.paymentId} · order ${order.payment.razorpayOrderId}` : "Paid entirely from the wallet — no online payment"}</p>
              {order.refund && <p className="mt-2 rounded-lg bg-coral-500/10 p-2 text-xs font-semibold">Refund {order.refund.status}: {formatInr(Math.round(order.refund.amountPaise / 100))} to {order.refund.destination === "wallet" ? "the wallet" : "the original payment method"}{order.refund.status === "failed" ? " — needs manual action" : ""}.</p>}
              {order.walletRefunded > 0 && <p className="mt-1 text-xs">{formatInr(order.walletRefunded)} returned to the customer&apos;s wallet.</p>}
            </div>
          </section>

          <SoonPanel title="Micro-route corridor" subtitle="Sample map — needs route planning and rider location">
            <div className="flex h-40 items-end rounded-xl bg-ocean-700/10 p-3 text-sm font-bold">Hub → {order.delivery.city} · distance and live traffic</div>
          </SoonPanel>
        </div>

        <div className="flex flex-col gap-5 xl:col-span-4">
          <section aria-labelledby="life-h" className={panel}>
            <div className="flex items-center justify-between"><h2 id="life-h" className="font-display text-lg font-bold">Order lifecycle</h2><span className="text-xs font-semibold">{order.status === "cancelled" ? "Cancelled" : `Step ${Math.max(1, step + 1)} of 5`}</span></div>
            <ol className="mt-3 flex flex-col gap-3 border-l-2 border-ocean-700/15 pl-5" data-testid="timeline">
              {order.history.map((h) => (
                <li key={`${h.status}-${h.at}`} className="relative">
                  <span aria-hidden="true" className="absolute -left-[1.85rem] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-teal-700 text-white"><CircleCheck className="h-3 w-3" /></span>
                  <p className="text-[11px] font-bold uppercase text-teal-700 dark:text-teal-300">{formatWhen(h.at)} · done</p>
                  <p className="text-sm font-bold">{STATUS_LABEL[h.status]}</p>
                </li>
              ))}
              {order.active && next && (
                <li className="relative rounded-lg bg-coral-500/10 p-2">
                  <span aria-hidden="true" className="absolute -left-[1.85rem] top-3 h-4 w-4 animate-pulse rounded-full bg-coral-600 motion-reduce:animate-none" />
                  <p className="text-[11px] font-bold uppercase text-coral-600 dark:text-coral-300">Now · action needed</p>
                  <p className="text-sm font-bold">{VERB[next]}</p>
                  <p className="text-xs">Next status: {STATUS_LABEL[next]}</p>
                </li>
              )}
              {order.active && FLOW.slice(step + 2).map((s) => (
                <li key={s} className="relative opacity-60"><span aria-hidden="true" className="absolute -left-[1.85rem] top-1 h-4 w-4 rounded-full bg-ocean-700/25" /><p className="text-[11px] font-bold uppercase">Next up</p><p className="text-sm font-bold">{STATUS_LABEL[s]}</p></li>
              ))}
            </ol>
          </section>

          {detail.complaint && (
            <section aria-labelledby="comp-h" className={panel} data-testid="complaint-card">
              <h2 id="comp-h" className="font-display text-lg font-bold">Complaint on this order</h2>
              <p className="mt-1 text-sm font-semibold">{reasonLabel(detail.complaint.reason)} · <span className="capitalize">{detail.complaint.status}</span></p>
              {detail.complaint.note && <p className="mt-1 rounded-lg bg-ocean-700/5 p-2 text-sm dark:bg-white/5">“{detail.complaint.note}”</p>}
              <Link to="/admin/claims" className="mt-2 inline-flex min-h-11 items-center text-sm font-bold underline">Open complaints</Link>
            </section>
          )}
          {order.review && (
            <section aria-labelledby="rev-h" className={panel}>
              <h2 id="rev-h" className="font-display text-lg font-bold">Customer review</h2>
              <p className="mt-1 flex items-center gap-1 text-sm font-bold"><Star className="h-4 w-4 fill-current text-teal-700" aria-hidden="true" /> {order.review.overall} / 5</p>
              {order.review.comment && <p className="mt-1 text-sm">“{order.review.comment}”</p>}
            </section>
          )}

          <SoonPanel title="Cold-chain telemetry" subtitle="Sample — needs a temperature probe in the box">
            <p className="font-display text-3xl font-bold">— °C</p><p className="text-xs">Probe reading, safe band and water-to-crate time appear here.</p>
          </SoonPanel>
          <section aria-label="Assigned captain" className={panel} data-testid="assigned-captain">
            <h2 className="font-display text-lg font-bold">Assigned captain</h2>
            {detail.driverId ? (
              <p className="mt-2 text-sm"><Link to={`/admin/drivers/${detail.driverId}`} className="font-bold underline" data-testid="captain-link">{detail.driverName}</Link>{detail.driverAssignedAt ? <span className="text-xs"> · handed over {formatWhen(detail.driverAssignedAt)}</span> : null}</p>
            ) : (
              <p className="mt-2 text-sm">No captain yet. {order.active && canWrite ? <Link to="/admin/dispatch" className="font-bold underline">Open fleet dispatch</Link> : null}</p>
            )}
          </section>

          <section aria-labelledby="note-h" className={panel}>
            <div className="flex items-center justify-between"><h2 id="note-h" className="font-display text-lg font-bold">Ops notes</h2><span className="text-xs">Staff only</span></div>
            <label htmlFor="ops-note" className="mt-2 block text-xs font-semibold">Dispatch &amp; handling note</label>
            <textarea id="ops-note" value={note} maxLength={500} readOnly={!canWrite} onChange={(event) => setNote(event.target.value)} rows={3} className="mt-1 w-full rounded-lg bg-ocean-700/5 p-3 text-sm focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/5" />
            <div className="mt-2 flex items-center justify-between gap-2">
              {canWrite && <button type="button" onClick={() => void saveNote()} disabled={busy || note.trim() === (detail.opsNote ?? "")} className="min-h-11 rounded-lg bg-ocean-800 px-5 text-sm font-bold text-white disabled:opacity-50">Save note</button>}
              <span className="text-xs italic">{detail.opsNote && detail.opsNoteBy && detail.opsNoteAt ? `${detail.opsNoteBy.replace("@staff.zenfresh.invalid", "")} · ${formatWhen(detail.opsNoteAt)}` : "No note yet"}</span>
            </div>
          </section>
        </div>
      </div>

      {actions && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-ocean-900/10 bg-white/95 px-4 py-3 shadow-xl backdrop-blur-md print:hidden dark:border-white/10 dark:bg-ocean-900/95">
          <div className="mx-auto flex max-w-[1500px] flex-col items-center justify-between gap-3 md:flex-row">
            <p className="text-sm"><span className="font-display font-bold">{next ? `${VERB[next]} — order #${order.ref}?` : `Order #${order.ref}`}</span><span className="block text-xs">{next ? `Moves it to “${STATUS_LABEL[next]}”. The customer sees the new status on their order page.` : "Nothing further to do."}</span></p>
            <div className="flex flex-wrap gap-2">{actions}</div>
          </div>
        </div>
      )}
    </div>
  );
}
